require('url-search-params-polyfill') // eslint-disable-line import/no-unassigned-import
const {acronym} = require('@goodgamestudios/game-alias')
const {getDoNotShowMessages} = require('./window-handlers')
const {CXF_DIALOG_OPEN} = require('./cxf-event-constants')
const {
    appendLoginParameters,
    ANNOUNCEMENTS_LOCALSTORAGE_KEY,
    hashCode,
    getHashKey,
    logger
} = require('./utils')

const alreadyShown = (loginData) => {
    const {game, playername, network, instance} = loginData
    const hash = getHashKey(game, playername, network, instance)
    return getDoNotShowMessages(hash)
}

const filterUnreadForceOpen = (alreadyShown, forceOpenMessages = false) => (
    msgs
) => {
    return msgs
        .filter(
            (message) =>
                !alreadyShown.includes(
                    hashCode(message.title + message.message)
                )
        )
        .filter((message) => Boolean(message.force_open) === forceOpenMessages)
}

const hasAnnouncements = (dtoJSON) => {
    return (dtoJSON && dtoJSON.Count > 0) || false
}

const handleAnnouncementMessage = (
    basepath,
    context,
    forceOpenMessages = false
) => (json, {network, instance, lang, playername}) => {
    const {game} = context
    if (!hasAnnouncements(json)) return

    const alreadyShownAnnouncements = alreadyShown({
        game,
        playername,
        network,
        instance
    })

    const announcements = filterUnreadForceOpen(
        alreadyShownAnnouncements,
        forceOpenMessages
    )(json.Items)

    if (!announcements || announcements.length === 0) {
        logger.info('No valid announcements to show')
        return
    }

    logger.info(`Received ${announcements.length} valid announcements`)

    const formattedAnnouncements = announcements.map((a) => ({
        ...a,
        body: a.message
    }))

    localStorage.setItem(
        ANNOUNCEMENTS_LOCALSTORAGE_KEY,
        JSON.stringify(formattedAnnouncements)
    )

    const hash = getHashKey(game, playername, network, instance)
    const parameters = {game, lang, network, instance, hash}
    const URLparams = new URLSearchParams(parameters)
    const templateUrl = `${basepath}/${game}_announcement.html?${URLparams.toString()}`

    logger.info(`templateUrl - ${templateUrl}`)
    context.emit(CXF_DIALOG_OPEN, templateUrl)
}

// Conditions that we can filter the display of announcements based on the player data
const getAnnouncementFilters = (loginData) => {
    return {
        level: loginData.level
    }
}

const loadCurrentMessages = async (path, handler, loginData) => {
    const filters = getAnnouncementFilters(loginData)

    path = appendLoginParameters(path, loginData, filters)
    return fetch(path)
        .then((r) => r.json())
        .then((data) => {
            if (Object.keys(data).length === 0 || data.Count === 0) {
                logger.info(`${path} shows empty response`)
                return
            }

            return handler(data, loginData)
        })
        .catch((error) => logger.log(`failed loading ${path}: ${error}`))
}

const getRootGamePayload = (data, adapter) => {
    if (!data || !Array.isArray(data) || data.length < 4) {
        logger.warn(
            'Received invalid payload from game - %O \n the valid payload is [userdata, lang, network, instance]',
            data
        )
        return
    }

    const [userdata, lang, network, instance] = data

    return {
        userdata: adapter.getPlayerInfo(userdata),
        lang,
        network,
        instance
    }
}

const countUnread = (loginData, announcements) => {
    const filteredAnnouncements = filterUnreadForceOpen(
        alreadyShown(loginData)
    )(announcements)
    return filteredAnnouncements.length
}

const getNewAnnouncements = (loginData) => {
    logger.log('getNewAnnouncements - loginData: %O', loginData)

    const countUnreadAnnouncements = (announcements) => {
        let announcementCount = 0
        if (hasAnnouncements(announcements)) {
            announcementCount = countUnread(loginData, announcements.Items)
            setAnnouncementsCache(announcements)
        }

        logger.log(` Has announcements: `, announcementCount > 0)
        return announcementCount
    }

    return loadCurrentMessages(
        process.env.MESSAGES_API + loginData.gameAcronym,
        countUnreadAnnouncements,
        loginData
    )
}

const loadMessages = (context) => (loginData, forceOpenMessages = false) => {
    const handleMessages = handleAnnouncementMessage(
        process.env.ANNOUNCEMENT_TEMPLATE,
        context,
        forceOpenMessages
    )

    return loadCurrentMessages(
        process.env.MESSAGES_API + loginData.gameAcronym,
        handleMessages,
        loginData
    )
}

const loadLoginMessages = (context) => (loginData) => {
    const forceOpenMessages = true
    return loadMessages(context)(loginData, forceOpenMessages)
}

// ========= Announcements and login data cache section =========

let cachedAnnouncements

const setAnnouncementsCache = (annoucement) => {
    cachedAnnouncements = annoucement
}

const getAnnouncementsCache = () => {
    if (!cachedAnnouncements || Object.keys(cachedAnnouncements).length === 0) {
        logger.warn(
            'getAnnouncementsCache - annoucement data has not been set yet',
            cachedAnnouncements
        )
        return
    }

    return cachedAnnouncements
}

const preservedLoginData = {}

const setLoginData = (context, adapter) => (loginData) => {
    if (!loginData && Object.keys(preservedLoginData).length === 0) {
        logger.warn(
            'saveLoginData - the user data has not been set/initialized %O',
            preservedLoginData
        )
        return
    }

    const gamePayload = getRootGamePayload(loginData, adapter)

    if (loginData && gamePayload) {
        Object.assign(preservedLoginData, {
            lang: gamePayload.lang,
            network: gamePayload.network,
            instance: gamePayload.instance,
            game: context.game,
            gameAcronym: acronym(context.game),
            playername: gamePayload.userdata.playername,
            level: gamePayload.userdata.level
        })
    }
}

const getLoginData = () => {
    if (!preservedLoginData || Object.keys(preservedLoginData).length === 0) {
        logger.warn(
            'getLoginData - login data has not been set yet',
            preservedLoginData
        )
        return
    }

    return preservedLoginData
}

module.exports = {
    getNewAnnouncements,
    getAnnouncementsCache,
    setLoginData,
    getLoginData,
    loadMessages,
    loadLoginMessages
}

module.exports.__internals = {
    alreadyShown,
    countUnread,
    filterUnreadForceOpen,
    getAnnouncementFilters,
    getRootGamePayload,
    handleAnnouncementMessage,
    hasAnnouncements,
    loadCurrentMessages,
    setAnnouncementsCache
}
